import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Box,
  Grid,
  Chip,
  Rating,
  CircularProgress,
  Alert,
  Paper,
  Divider,
  Button,
  Stack,
  TextField,
} from '@mui/material';
import {
  ArrowBack,
  CalendarToday,
  Category,
  Person,
  Description,
  Comment,
  Star,
  Feedback,
} from '@mui/icons-material';
import api from '../../utils/api';

const PublicCaseDetail = () => {
  const { caseId, email } = useParams();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rating, setRating] = useState(0);
  const [feedbackText, setFeedbackText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [feedbackSuccess, setFeedbackSuccess] = useState(false);

  useEffect(() => {
    if (caseId) {
      fetchCaseDetail();
    }
  }, [caseId, email]);

  const fetchCaseDetail = async () => {
    try {
      setLoading(true);
      setError('');
      
      let path;
      if (email && caseId) {
        path = `/tickets/cases/public/${encodeURIComponent(caseId)}/${encodeURIComponent(email)}`;
      } else if (caseId) {
        path = `/tickets/cases/public/${encodeURIComponent(caseId)}`;
      } else {
        setError('Case ID is required');
        setLoading(false);
        return;
      }
      
      const response = await api.get(path);
      setCaseData(response.data);
    } catch (err) {
      console.error('Error fetching case detail:', err);
      if (err.response?.status === 404) {
        setError(email ? 'Case not found or email does not match' : 'Case not found');
      } else {
        setError('Failed to load case details. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleSubmitFeedback = async () => {
    if (rating === 0) {
      alert('Please select a rating before submitting.');
      return;
    }

    setSubmitting(true);
    setFeedbackSuccess(false);

    try {
      const formattedFeedback = `${feedbackText.trim()}`;
      
      const requestBody = {
        id: caseData.new_caseid,
        new_ratingstars: rating,
        new_feedback: formattedFeedback
      };

      await api.post(
        '/tickets/updatecase',
        requestBody
      );

      setCaseData(prev => ({
        ...prev,
        new_ratingstars: rating,
        new_feedback: formattedFeedback
      }));

      setFeedbackSuccess(true);
      alert('Feedback submitted successfully!');
    } catch (error) {
      console.error('Error submitting feedback:', error);
      alert('Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '80vh',
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/public-cases')}
          variant="contained"
        >
          Back to Cases
        </Button>
      </Container>
    );
  }

  if (!caseData) {
    return null;
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>

      {/* Case Header */}
      <Paper
        sx={{
          p: 4,
          mb: 3,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 4 }}>
          {/* Left: Title and metadata */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="h3" sx={{ fontWeight: 700, mb: 2 }}>
              {caseData.new_name || 'Untitled Case'}
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <Chip
                icon={<CalendarToday />}
                label={formatDate(caseData.createdon)}
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
              />
              {caseData.new_ownersrnng && (
                <Chip
                  icon={<Person />}
                  label={caseData.new_ownersrnng}
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                />
              )}
            </Stack>
          </Box>

          {/* Right: Support Comments */}
          {caseData.new_supportcomments && (
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Comment sx={{ mr: 1, color: 'success.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Support Comments
                </Typography>
              </Box>
              <Paper
                sx={{
                  p: 2,
                  bgcolor: 'rgba(255, 255, 255, 0.95)',
                  color: 'text.primary',
                }}
              >
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {caseData.new_supportcomments}
                </Typography>
              </Paper>
            </Box>
          )}
        </Box>
      </Paper>

      <Grid container spacing={3}>
        {/* Rating & Feedback Card - Left Side */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', background: 'linear-gradient(135deg, rgba(255, 193, 7, 0.1) 0%, rgba(255, 152, 0, 0.1) 100%)', border: '2px solid', borderColor: 'warning.main' }}>
            <CardContent>
              {/* Rating Section */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Star sx={{ mr: 1, color: 'warning.main' }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {caseData.new_ratingstars ? 'Your Rating' : 'Rate Your Experience'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Rating
                    value={caseData.new_ratingstars || rating || 0}
                    onChange={(event, newValue) => !caseData.new_ratingstars && setRating(newValue)}
                    readOnly={!!caseData.new_ratingstars}
                    size="large"
                  />
                  <Typography variant="h4" sx={{ fontWeight: 700, color: 'warning.main' }}>
                    {caseData.new_ratingstars || rating || 0}/5
                  </Typography>
                </Box>
                {caseData.new_ratingstars && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    You have already submitted your rating
                  </Alert>
                )}
              </Box>

              {/* Feedback Section */}
              <Divider sx={{ my: 2 }} />
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Feedback sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {caseData.new_feedback ? 'Your Feedback' : 'Share Your Feedback'}
                  </Typography>
                </Box>
                {caseData.new_feedback ? (
                  <Paper
                    sx={{
                      p: 2,
                      bgcolor: 'rgba(255, 255, 255, 0.8)',
                      border: '1px solid',
                      borderColor: 'primary.light',
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        whiteSpace: 'pre-wrap',
                        fontStyle: 'italic',
                      }}
                    >
                      "{caseData.new_feedback}"
                    </Typography>
                  </Paper>
                ) : (
                  <>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      placeholder="Share your experience with us..."
                      value={feedbackText}
                      onChange={(e) => setFeedbackText(e.target.value)}
                      sx={{
                        bgcolor: 'rgba(255, 255, 255, 0.8)',
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: 'primary.light',
                          },
                        },
                      }}
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleSubmitFeedback}
                      disabled={submitting || rating === 0}
                      fullWidth
                      sx={{ py: 1.5, fontWeight: 600 }}
                    >
                      {submitting ? 'Submitting...' : feedbackSuccess ? 'Feedback Submitted ✓' : 'Submit Feedback'}
                    </Button>
                    {feedbackSuccess && (
                      <Alert severity="success" sx={{ mt: 2 }}>
                        Thank you for your feedback!
                      </Alert>
                    )}
                  </>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Categories Card - Right Side */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Category sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Categories
                </Typography>
              </Box>
              <Stack spacing={1}>
                {caseData.new_cat1srng && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Category 1
                    </Typography>
                    <Chip
                      label={caseData.new_cat1srng}
                      color="primary"
                      sx={{ mt: 0.5 }}
                    />
                  </Box>
                )}
                {caseData.new_cat2srng && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Category 2
                    </Typography>
                    <Chip
                      label={caseData.new_cat2srng}
                      color="secondary"
                      sx={{ mt: 0.5 }}
                    />
                  </Box>
                )}
                {caseData.new_cat3srng && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Category 3
                    </Typography>
                    <Chip label={caseData.new_cat3srng} sx={{ mt: 0.5 }} />
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Description Card - Full Width Below */}
        {caseData.new_description && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Description sx={{ mr: 1, color: 'info.main' }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Issue Description
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {caseData.new_description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Container>
  );
};

export default PublicCaseDetail;
